import * as Sentry from '@sentry/react'
import { createContext, useContext, useState, useEffect } from 'react'
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth'
import { auth } from '../lib/firebase'
import { refreshProducts } from '../data/products'

const AuthContext = createContext()

export function useAuth() {
  return useContext(AuthContext)
}

const PROJECT_ID = 'desres-kitchen-v2'
const REST_BASE = `https://firestore.googleapis.com/v1/projects/${PROJECT_ID}/databases/(default)/documents`

function parseVal(v) {
  if (!v) return v
  if ('booleanValue' in v) return v.booleanValue
  if ('doubleValue' in v) return Number(v.doubleValue)
  if ('integerValue' in v) return Number(v.integerValue)
  if ('stringValue' in v) return v.stringValue
  if ('timestampValue' in v) return v.timestampValue
  if ('arrayValue' in v) return (v.arrayValue.values || []).map(parseVal)
  if ('mapValue' in v) return parseFields(v.mapValue.fields)
  if ('nullValue' in v) return null
  return v
}

function parseFields(fields) {
  if (!fields) return {}
  const obj = {}
  for (const [k, v] of Object.entries(fields)) obj[k] = parseVal(v)
  return obj
}

function toVal(v) {
  if (v === null || v === undefined) return { nullValue: null }
  if (typeof v === 'boolean') return { booleanValue: v }
  if (typeof v === 'number') return { doubleValue: v }
  if (typeof v === 'string') return { stringValue: v }
  if (Array.isArray(v)) return { arrayValue: { values: v.map(toVal) } }
  if (typeof v === 'object') return { mapValue: { fields: toFields(v) } }
  return { stringValue: String(v) }
}

function toFields(obj) {
  const fields = {}
  for (const [k, v] of Object.entries(obj)) fields[k] = toVal(v)
  return fields
}

async function restGet(path, token) {
  const headers = {}
  if (token) headers['Authorization'] = `Bearer ${token}`
  const res = await fetch(`${REST_BASE}/${path}`, { headers })
  if (res.status === 404) return null
  if (!res.ok) throw new Error(`REST read ${path}: ${res.status}`)
  return parseFields((await res.json()).fields)
}

async function restPatch(path, obj, token) {
  const res = await fetch(`${REST_BASE}/${path}`, {
    method: 'PATCH',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ fields: toFields(obj) }),
  })
  if (!res.ok) throw new Error(`REST write ${path}: ${res.status}`)
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [connectionError, setConnectionError] = useState(false)

  async function fetchUserProfile(uid) {
    setConnectionError(false)
    try {
      const token = await auth.currentUser?.getIdToken()
      if (!token) { setUserProfile(null); return null }

      const data = await restGet(`users/${uid}`, token)
      if (!data) { setUserProfile(null); return null }

      const profile = { uid, ...data }
      const allDepts = ['kitchen', 'bakery', 'yawehs', 'tuckshop']

      // Designated admin account — ensure admin access is always set
      if (data.email === 'blisssly94@gmail.com' && data.role !== 'admin') {
        const fix = { role: 'admin', isApproved: true, allowedDepartments: allDepts }
        await restPatch(`users/${uid}`, fix, token).catch(err => {
          console.warn('[AuthContext] admin auto-fix (designated) failed:', err?.message)
          Sentry.captureException(err, { extra: { fn: 'AuthContext.adminAutoFix.designated', uid, email: data.email } })
        })
        Object.assign(profile, fix)
      }

      // Self-heal: if admin user is missing departments, fix it
      if (profile.role === 'admin') {
        const current = profile.allowedDepartments || []
        if (!profile.isApproved || allDepts.some(d => !current.includes(d))) {
          const fix = { allowedDepartments: allDepts, isApproved: true }
          await restPatch(`users/${uid}`, fix, token).catch(err => {
            console.warn('[AuthContext] admin self-heal failed:', err?.message)
            Sentry.captureException(err, { extra: { fn: 'AuthContext.adminAutoFix.selfHeal', uid, email: data.email } })
          })
          Object.assign(profile, fix)
        }
      }

      setUserProfile(profile)
      return profile
    } catch (err) {
      console.error('Failed to fetch user profile:', err)
      setConnectionError(true)
      setUserProfile(null)
      return null
    }
  }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser)
      if (firebaseUser) {
        Sentry.setUser({ email: firebaseUser.email, id: firebaseUser.uid })
        await fetchUserProfile(firebaseUser.uid)
        // Trigger a one-shot product refresh after login. This is the
        // hook that propagates seed-version bumps (e.g. v3) to devices
        // whose initial products.js initProducts() ran during the
        // unauthenticated window and short-circuited at waitForAuth.
        // refreshProducts is fire-and-forget, idempotent per session,
        // and self-guards against double seed checks.
        refreshProducts().catch(err => {
          console.warn('[Auth] post-login refreshProducts failed:', err?.message)
        })
      } else {
        Sentry.setUser(null)
        setUserProfile(null)
        setConnectionError(false)
      }
      setLoading(false)
    })
    return unsub
  }, [])

  async function register(email, password, displayName) {
    const cred = await createUserWithEmailAndPassword(auth, email, password)
    const token = await cred.user.getIdToken()

    // Firestore rules forbid self-assignment of role / isApproved on create.
    // Admins promote new users via the Users tab. The hardcoded admin
    // (blisssly94@gmail.com) is auto-promoted by fetchUserProfile's self-heal.
    const userDoc = {
      email,
      displayName,
      allowedDepartments: [],
      createdAt: new Date().toISOString(),
    }

    await restPatch(`users/${cred.user.uid}`, userDoc, token)
    await fetchUserProfile(cred.user.uid)
    return { isFirstUser: false }
  }

  async function login(email, password) {
    const cred = await signInWithEmailAndPassword(auth, email, password)
    const profile = await fetchUserProfile(cred.user.uid)
    return profile
  }

  async function logout() {
    await signOut(auth)
    setUser(null)
    setUserProfile(null)
  }

  async function refreshProfile() {
    if (user) {
      return await fetchUserProfile(user.uid)
    }
    return null
  }

  const value = {
    user,
    userProfile,
    loading,
    connectionError,
    login,
    register,
    logout,
    refreshProfile,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
